import { IAccount, PlanStatus } from "~/types/account";
import { ILimit, IPlan, Limits, SubscriptionType } from "~/types/billing";
import { getTotalPlanCost } from "./numbers";

export const DISABLE_PAYMENTS = false;
export const DISABLE_TRIAL_FEES = true;

// Stripe confirms payment before the subscription webhook updates our backend,
// so the account can briefly read as "unsubscribed" right after a successful
// payment. This flag marks that window so guards don't bounce a paying user
// back into onboarding/checkout (post-payment redirect loop).
const RECENT_PAYMENT_KEY = "recent_payment_at";
const RECENT_PAYMENT_GRACE_MS = 5 * 60 * 1000;

export const markRecentPayment = () => {
  localStorage.setItem(RECENT_PAYMENT_KEY, String(Date.now()));
};

export const clearRecentPayment = () => {
  localStorage.removeItem(RECENT_PAYMENT_KEY);
};

export const hasRecentPayment = (): boolean => {
  const raw = localStorage.getItem(RECENT_PAYMENT_KEY);
  if (!raw) return false;
  const at = Number(raw);
  if (!Number.isFinite(at)) return false;
  return Date.now() - at < RECENT_PAYMENT_GRACE_MS;
};

export const getCheckoutAction = (
  account: IAccount | null,
  plan: IPlan | undefined
): "trial" | "downgrade" | "upgrade" | "firstSubscription" => {
  const isDowngrade =
    account?.active_subscription?.plan &&
    getTotalPlanCost(plan) < getTotalPlanCost(account.active_subscription.plan);

  if (!account?.has_subscribed_before) {
    if (!!plan?.trial_days) {
      return "trial";
    }

    return "firstSubscription";
  }

  if (isDowngrade) {
    return "downgrade";
  }

  return "upgrade";
};

export const isFeatureEnabledOnPlan = (
  limitType: Limits,
  plan: IPlan | undefined
) => {
  if (DISABLE_PAYMENTS) return true;

  const limits: ILimit | Record<any, any> = plan?.limits || {};

  if (limitType === Limits.PAID_PLAN) {
    return !!plan;
  }

  if (!limits) return false;

  return !!limits[limitType];
};

export const hasActiveAnnualPlan = (plan?: IPlan | null): boolean => {
  return (
    (plan?.status === PlanStatus.ACTIVE &&
      plan?.interval === SubscriptionType.ANNUAL) ??
    false
  );
};
